import React from 'react';
import {
  Datagrid,
  FunctionField,
  NumberField,
  ResourceContextProvider,
  TextField,
} from 'react-admin';
import { nameColumnStyling } from '../../../utils/nameFieldSx';
import InfiniteList from '../components/InfiniteList';
import { actionsHeaderLabel, CategoryRowActions } from './CategoryActions';
import { ChangeParentBulkActionButton } from './ChangeParentBulkActionButton';
import { type ExerciseTabListProps } from './ExerciseList';
import {
  ExerciseBaseFilters,
  ExerciseTypeCategoryFilters,
  ExerciseTypeCategoryGroupFilters,
  ExerciseTypeFilters,
} from './filters';

export const ExerciseTypeCategoryGroupList: React.FC<ExerciseTabListProps> = ({
  filterDefaultValues,
  disableSyncWithLocation = true,
}) => (
  <ResourceContextProvider value="ExerciseTypeCategoryGroup">
    <InfiniteList
      disableSyncWithLocation={disableSyncWithLocation}
      filters={<ExerciseTypeCategoryGroupFilters />}
      filterDefaultValues={filterDefaultValues}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" sortable={false} sx={nameColumnStyling} />
        <NumberField
          source="exerciseTypeCategories.count"
          label="Movement Type Categories"
          sortable={false}
        />
        <FunctionField
          label={actionsHeaderLabel}
          sortable={false}
          render={() => (
            <CategoryRowActions resource="ExerciseTypeCategoryGroup" />
          )}
        />
      </Datagrid>
    </InfiniteList>
  </ResourceContextProvider>
);

export const ExerciseTypeCategoryList: React.FC<ExerciseTabListProps> = ({
  filterDefaultValues,
  disableSyncWithLocation = true,
}) => (
  <ResourceContextProvider value="ExerciseTypeCategory">
    <InfiniteList
      disableSyncWithLocation={disableSyncWithLocation}
      filters={<ExerciseTypeCategoryFilters />}
      filterDefaultValues={filterDefaultValues}
    >
      <Datagrid
        bulkActionButtons={
          <ChangeParentBulkActionButton
            config={{
              resource: 'ExerciseTypeCategory',
              itemLabel: 'Movement Type Category',
              parentResource: 'ExerciseTypeCategoryGroup',
              parentLabel: 'MTC Group',
            }}
          />
        }
      >
        <TextField source="name" sortable={false} sx={nameColumnStyling} />
        <TextField
          source="exerciseTypeCategoryGroup.name"
          label="MTC Group"
          sortable={false}
        />
        <NumberField
          source="exerciseTypes.count"
          label="Movement Types"
          sortable={false}
        />
        <FunctionField
          label={actionsHeaderLabel}
          sortable={false}
          render={() => <CategoryRowActions resource="ExerciseTypeCategory" />}
        />
      </Datagrid>
    </InfiniteList>
  </ResourceContextProvider>
);

export const ExerciseTypeList: React.FC<ExerciseTabListProps> = ({
  filterDefaultValues,
  disableSyncWithLocation = true,
}) => (
  <ResourceContextProvider value="ExerciseType">
    <InfiniteList
      disableSyncWithLocation={disableSyncWithLocation}
      filters={<ExerciseTypeFilters />}
      filterDefaultValues={filterDefaultValues}
    >
      <Datagrid
        bulkActionButtons={
          <ChangeParentBulkActionButton
            config={{
              resource: 'ExerciseType',
              itemLabel: 'Movement Type',
              parentResource: 'ExerciseTypeCategory',
              parentLabel: 'Movement Type Category',
            }}
          />
        }
      >
        <TextField
          source="name"
          label="Name"
          sortable={false}
          sx={nameColumnStyling}
        />
        <TextField
          source="exerciseTypeCategory.name"
          label="MTC"
          sortable={false}
        />
        <TextField
          source="exerciseTypeCategory.exerciseTypeCategoryGroup.name"
          label="MTC Group"
          sortable={false}
        />
        <NumberField
          source="exerciseBases.count"
          label="Base Movements"
          sortable={false}
        />
        <FunctionField
          label={actionsHeaderLabel}
          sortable={false}
          render={() => <CategoryRowActions resource="ExerciseType" />}
        />
      </Datagrid>
    </InfiniteList>
  </ResourceContextProvider>
);

export const ExerciseBaseList: React.FC<ExerciseTabListProps> = ({
  filterDefaultValues,
  disableSyncWithLocation = true,
}) => (
  <ResourceContextProvider value="ExerciseBase">
    <InfiniteList
      disableSyncWithLocation={disableSyncWithLocation}
      filters={<ExerciseBaseFilters />}
      filterDefaultValues={filterDefaultValues}
    >
      <Datagrid
        bulkActionButtons={
          <ChangeParentBulkActionButton
            config={{
              resource: 'ExerciseBase',
              itemLabel: 'Base Movement',
              parentResource: 'ExerciseType',
              parentLabel: 'Movement Type',
            }}
          />
        }
      >
        <TextField
          source="name"
          label="Name"
          sortable={false}
          sx={nameColumnStyling}
        />
        <TextField
          source="exerciseType.name"
          label="Movement Type"
          sortable={false}
        />
        <TextField
          source="exerciseType.exerciseTypeCategory.name"
          label="MTC"
          sortable={false}
        />
        <TextField
          source="exerciseType.exerciseTypeCategory.exerciseTypeCategoryGroup.name"
          label="MTC Group"
          sortable={false}
        />
        <NumberField source="progression" sortable={false} />
        <NumberField
          source="exercises.count"
          label="Movements"
          sortable={false}
        />
        <FunctionField
          label={actionsHeaderLabel}
          sortable={false}
          render={() => <CategoryRowActions resource="ExerciseBase" />}
        />
      </Datagrid>
    </InfiniteList>
  </ResourceContextProvider>
);
